import {
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Select,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconCalendarMonth,
  IconCalendarTime,
  IconFileExport,
  IconSearch,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
// Dùng xlsx-js-style (fork của xlsx) vì bản xlsx gốc bỏ qua style ô khi ghi file
import * as XLSX from "xlsx-js-style";
import { repositoryDelivery } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../service/getSelectApi";
import "./WarrantyReceptionProcessingReport.css";

type WarrantyAnalysisRow = {
  branch_id: number;
  period: string;
  tieu_chi: string;
  qty: number;
  rate: number;
};

// Style căn giữa cho ô Số lượng / Tỉ lệ (dùng textAlign để override style nội bộ MRT)
const METRIC_CELL_STYLE: React.CSSProperties = {
  fontSize: "13px",
  padding: "5px 12px",
  textAlign: "center",
  verticalAlign: "middle",
};

// Các tiêu chí (giờ là HÀNG) hiển thị theo đúng thứ tự yêu cầu
const CRITERIA_ROWS = [
  "A. Số lượng sản phẩm nhận bảo hành từ khách hàng",
  "A.1 Số lượng sản phẩm có hàng, trả hàng đúng cam kết",
  "A.2 Số lượng sản phẩm bảo hành sai cam kết",
  "B. Số lượng sản phẩm gửi bảo hành Nhà cung cấp",
  "B.1 Số lượng sản phẩm NCC trả đúng cam kết",
  "B.2 Số lượng sản phẩm NCC trả sai cam kết",
  "C. Số sản phẩm trả Khách hàng bị lỗi cũ (Lỗi cũ tính trong 15 ngày từ khi trả KH)",
];

// A.1, A.2, B.1... là dòng chi tiết -> để chữ thường;
// A., B., C. là dòng tổng -> giữ in đậm
const isSubCriteria = (criteria: string) => /^[A-Z]\.\d/.test(criteria?.trim());

// 2 branch_id ảo BE trả về: không phải chi nhánh mà là 2 nhóm tổng hợp riêng
const STOCK_BRANCH_ID = -1; // Hàng kho: chỉ 1 cột kỳ
const CUSTOMER_BRANCH_ID = -2; // Khách hàng: 2 cột kỳ nhưng chỉ hiện năm

// branch_id âm -> thay "-" thành "m" để accessorKey/id của cột luôn hợp lệ
const branchKey = (branchId: number) => String(branchId).replace("-", "m");

const metricKey = (
  branchId: number,
  periodIdx: number,
  metric: "qty" | "rate",
) => `b${branchKey(branchId)}_p${periodIdx}_${metric}`;

// ----- Style cho file Excel xuất ra -----
const EXCEL_HEADER_ROWS = 3; // Chi nhánh / Tháng-Năm / Số lượng - Tỉ lệ
// Màu nền 3 tầng header, đậm dần từ trên xuống
const EXCEL_HEADER_FILLS = ["1864AB", "1971C2", "228BE6"];
const EXCEL_SUMMARY_FILL = "E7F5FF"; // nền dòng tổng A. / B. / C.
const EXCEL_BORDER = {
  top: { style: "thin", color: { rgb: "868E96" } },
  bottom: { style: "thin", color: { rgb: "868E96" } },
  left: { style: "thin", color: { rgb: "868E96" } },
  right: { style: "thin", color: { rgb: "868E96" } },
};

const WarrantyReceptionProcessingReport = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<WarrantyAnalysisRow[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [height, setHeight] = useState(0);
  const [visible] = useDisclosure(false);
  // Map id chi nhánh -> tên chi nhánh (lấy từ getMasterOrgSelect)
  const [branchNameMap, setBranchNameMap] = useState<Record<string, string>>(
    {},
  );

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    thang1:
      _searchJson && checkedPinSearch
        ? _searchJson?.thang1
        : String(new Date().getMonth() + 1),
    thang2:
      _searchJson && checkedPinSearch
        ? _searchJson?.thang2
        : String(new Date().getMonth() + 1),
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );
  const [nam1, setNam1] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.nam1 : "2025",
    300,
  );
  const [nam2, setNam2] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.nam2 : "2026",
    300,
  );

  // Kỳ (tháng/năm) đang hiển thị trên bảng - chốt lại tại thời điểm bấm tìm kiếm
  const [month1ViewTable, setMonth1ViewTable] = useState<string>("");
  const [year1ViewTable, setYear1ViewTable] = useState<string>("");
  const [month2ViewTable, setMonth2ViewTable] = useState<string>("");
  const [year2ViewTable, setYear2ViewTable] = useState<string>("");

  const dataMonth = [
    { label: "Tháng 1", value: "1" },
    { label: "Tháng 2", value: "2" },
    { label: "Tháng 3", value: "3" },
    { label: "Tháng 4", value: "4" },
    { label: "Tháng 5", value: "5" },
    { label: "Tháng 6", value: "6" },
    { label: "Tháng 7", value: "7" },
    { label: "Tháng 8", value: "8" },
    { label: "Tháng 9", value: "9" },
    { label: "Tháng 10", value: "10" },
    { label: "Tháng 11", value: "11" },
    { label: "Tháng 12", value: "12" },
  ];

  // 2 kỳ hiển thị trên bảng
  const periods = useMemo(
    () => [
      {
        label: `Tháng ${month1ViewTable}/${year1ViewTable}`,
        value: `${month1ViewTable}/${year1ViewTable}`,
      },
      {
        label: `Tháng ${month2ViewTable}/${year2ViewTable}`,
        value: `${month2ViewTable}/${year2ViewTable}`,
      },
    ],
    [month1ViewTable, year1ViewTable, month2ViewTable, year2ViewTable],
  );

  // Danh sách chi nhánh theo thứ tự xuất hiện trong data,
  // riêng Hàng kho / Khách hàng luôn đẩy xuống 2 vị trí cuối cùng
  const branchIds = useMemo(() => {
    const seen: number[] = [];
    data.forEach((item) => {
      if (!seen.includes(item.branch_id)) seen.push(item.branch_id);
    });

    const orderWeight = (branchId: number) => {
      if (branchId === STOCK_BRANCH_ID) return 1; // kế cuối
      if (branchId === CUSTOMER_BRANCH_ID) return 2; // cuối cùng
      return 0;
    };

    return seen.sort((a, b) => orderWeight(a) - orderWeight(b));
  }, [data]);

  // Bảng tra cứu: branchId | period | tieu_chi -> { qty, rate }
  const lookup = useMemo(() => {
    const map = new Map<string, { qty: number; rate: number }>();
    data.forEach((item) => {
      const key = `${item.branch_id}|${item.period}|${(item.tieu_chi || "").trim()}`;
      map.set(key, { qty: item.qty, rate: item.rate });
    });
    return map;
  }, [data]);

  // Cấu trúc cột của từng branch: tên hiển thị + các kỳ (đã đổi nhãn) + vị trí cột trong Excel
  const branchLayouts = useMemo(() => {
    const getBranchLabel = (branchId: number) => {
      if (branchId === STOCK_BRANCH_ID) return "Hàng kho";
      if (branchId === CUSTOMER_BRANCH_ID) return "Khách hàng";
      return branchNameMap[String(branchId)] ?? `Chi nhánh ${branchId}`;
    };

    // Hàng kho chỉ có 1 cột kỳ -> lấy kỳ thực sự có dữ liệu trả về
    const getBranchPeriods = (branchId: number) => {
      if (branchId !== STOCK_BRANCH_ID) return periods;
      const hasData = periods.filter((p) =>
        data.some((d) => d.branch_id === branchId && d.period === p.value),
      );
      return hasData.length > 0
        ? [hasData[0]]
        : periods.slice(periods.length - 1);
    };

    // Khách hàng chỉ hiển thị năm, không hiển thị tháng
    const getPeriodLabel = (branchId: number, period: string, label: string) =>
      branchId === CUSTOMER_BRANCH_ID
        ? `Năm ${period.split("/")[1] ?? period}`
        : label;

    let start = 1; // cột 0 dành cho Tiêu chí
    return branchIds.map((branchId) => {
      const branchPeriods = getBranchPeriods(branchId).map((p) => ({
        ...p,
        label: getPeriodLabel(branchId, p.value, p.label),
      }));
      const layout = {
        branchId,
        label: getBranchLabel(branchId),
        periods: branchPeriods,
        start,
        width: branchPeriods.length * 2, // mỗi kỳ gồm Số lượng + Tỉ lệ
      };
      start += layout.width;
      return layout;
    });
  }, [branchIds, periods, branchNameMap, data]);

  // Mỗi HÀNG là 1 tiêu chí; giá trị các CỘT (branch x kỳ x metric) nằm cùng object
  const tableData = useMemo(() => {
    if (branchLayouts.length === 0) return [];
    return CRITERIA_ROWS.map((criteria) => {
      const row: Record<string, any> = { id: criteria, tieu_chi: criteria };
      branchLayouts.forEach((branch) => {
        branch.periods.forEach((p, i) => {
          const found = lookup.get(
            `${branch.branchId}|${p.value}|${criteria.trim()}`,
          );
          row[metricKey(branch.branchId, i, "qty")] = found ? found.qty : "";
          row[metricKey(branch.branchId, i, "rate")] = found
            ? `${found.rate}%`
            : "";
        });
      });
      return row;
    });
  }, [branchLayouts, lookup]);

  // Cột: Tiêu chí (ghim trái) + nhóm Chi nhánh -> nhóm Tháng -> Số lượng / Tỉ lệ
  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const metricCols = (branchId: number, periodIdx: number) => [
      {
        header: "Số lượng",
        accessorKey: metricKey(branchId, periodIdx, "qty"),
        size: 95,
        mantineTableHeadCellProps: {
          align: "center" as const,
          style: { textAlign: "center" as const },
        },
        mantineTableBodyCellProps: { style: METRIC_CELL_STYLE },
      },
      {
        header: "Tỉ lệ",
        accessorKey: metricKey(branchId, periodIdx, "rate"),
        size: 95,
        mantineTableHeadCellProps: {
          align: "center" as const,
          style: { textAlign: "center" as const },
        },
        mantineTableBodyCellProps: { style: METRIC_CELL_STYLE },
      },
    ];

    const branchCols: MRT_ColumnDef<any>[] = branchLayouts.map((branch) => ({
      header: branch.label,
      id: `branch_${branchKey(branch.branchId)}`,
      columns: branch.periods.map((p, i) => ({
        header: p.label,
        id: `branch_${branchKey(branch.branchId)}_p${i}`,
        columns: metricCols(branch.branchId, i),
      })),
    }));

    return [
      {
        // Nhóm 3 tầng để lấp 2 ô header trống phía trên cột Tiêu chí
        header: "Chi nhánh",
        id: "tieu_chi_branch",
        columns: [
          {
            header: "Tháng/Năm",
            id: "tieu_chi_period",
            columns: [
              {
                header: "Tiêu chí",
                accessorKey: "tieu_chi",
                size: 390,
                enableColumnActions: false,
                mantineTableBodyCellProps: ({ row }) => {
                  const isSub = isSubCriteria(row.original.tieu_chi);
                  return {
                    style: {
                      whiteSpace: "normal",
                      // Dòng tổng A./B./C. nổi hẳn lên so với dòng chi tiết
                      fontWeight: isSub ? 400 : 700,
                      fontSize: isSub ? "13px" : "14.5px",
                    },
                  };
                },
              },
            ],
          },
        ],
      },
      ...branchCols,
    ];
  }, [branchLayouts]);

  // MRT giữ nguyên columnOrder cũ và nối cột mới vào CUỐI danh sách, nên khi lọc lại
  // ra thêm chi nhánh thì chi nhánh đó bị đẩy xuống sau Hàng kho / Khách hàng.
  // -> tự kiểm soát columnOrder theo đúng thứ tự của mảng columns.
  const columnOrder = useMemo(() => {
    const collectLeafIds = (cols: MRT_ColumnDef<any>[]): string[] =>
      cols.flatMap((col) =>
        col.columns
          ? collectLeafIds(col.columns)
          : [String(col.id ?? col.accessorKey)],
      );
    return collectLeafIds(columns);
  }, [columns]);

  // Lấy danh sách chi nhánh -> tạo map id -> tên
  const fetchBranchNames = async () => {
    const res = await getMasterOrgSelect();
    if (res && res.length > 0) {
      const map: Record<string, string> = {};
      res.forEach((item) => {
        if (item.value) map[item.value] = item.text || item.name || item.value;
      });
      setBranchNameMap(map);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    setMonth1ViewTable(search.thang1);
    setYear1ViewTable(nam1);
    setMonth2ViewTable(search.thang2);
    setYear2ViewTable(nam2);

    try {
      let url = `/api/v1/DefectiveItemsList/get-warranty-analysis-report`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (nam1.length > 0) {
        params.append("nam1", nam1);
      }

      if (nam2.length > 0) {
        params.append("nam2", nam2);
      }

      if (keySearch.length > 0) {
        params.append("KeySearch", keySearch?.trim());
      }

      const queryString = params.toString();
      if (queryString.length > 0) {
        url += `?${queryString}`;
      }

      const res =
        await repositoryDelivery.get<MessageResponse<WarrantyAnalysisRow[]>>(
          url,
        );

      if (res && res.success) {
        setData(res.data ?? []);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
          nam1,
          nam2,
        }),
      );
    }
  };

  // Xuất Excel đúng cấu trúc bảng đang hiển thị:
  // 3 tầng header (Chi nhánh / Tháng-Năm / Số lượng - Tỉ lệ), mỗi tiêu chí là 1 hàng
  const handleExportExcel = () => {
    if (tableData.length === 0) {
      NotificationExtension.Fails("Không có dữ liệu để xuất Excel !");
      return;
    }

    // Số cột mỗi branch không giống nhau (Hàng kho chỉ 1 kỳ) -> lấy theo branchLayouts
    const totalCols =
      1 + branchLayouts.reduce((sum, branch) => sum + branch.width, 0);

    const headerBranch = new Array(totalCols).fill("");
    const headerPeriod = new Array(totalCols).fill("");
    const headerMetric = new Array(totalCols).fill("");
    const merges: XLSX.Range[] = [];

    headerBranch[0] = "Chi nhánh";
    headerPeriod[0] = "Tháng/Năm";
    headerMetric[0] = "Tiêu chí";

    branchLayouts.forEach((branch) => {
      headerBranch[branch.start] = branch.label;
      merges.push({
        s: { r: 0, c: branch.start },
        e: { r: 0, c: branch.start + branch.width - 1 },
      });

      branch.periods.forEach((p, periodIdx) => {
        const periodStart = branch.start + periodIdx * 2;
        headerPeriod[periodStart] = p.label;
        merges.push({
          s: { r: 1, c: periodStart },
          e: { r: 1, c: periodStart + 1 },
        });
        headerMetric[periodStart] = "Số lượng";
        headerMetric[periodStart + 1] = "Tỉ lệ";
      });
    });

    const bodyRows = tableData.map((row) => {
      const line = new Array(totalCols).fill("");
      line[0] = row.tieu_chi;
      branchLayouts.forEach((branch) => {
        branch.periods.forEach((_, periodIdx) => {
          const col = branch.start + periodIdx * 2;
          line[col] = row[metricKey(branch.branchId, periodIdx, "qty")] ?? "";
          line[col + 1] =
            row[metricKey(branch.branchId, periodIdx, "rate")] ?? "";
        });
      });
      return line;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
      headerBranch,
      headerPeriod,
      headerMetric,
      ...bodyRows,
    ]);
    worksheet["!merges"] = merges;
    worksheet["!cols"] = [
      { wch: 60 },
      ...new Array(totalCols - 1).fill({ wch: 13 }),
    ];
    worksheet["!rows"] = [{ hpt: 26 }, { hpt: 22 }, { hpt: 22 }];

    const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "");
    for (let r = range.s.r; r <= range.e.r; r++) {
      const isHeaderRow = r < EXCEL_HEADER_ROWS;
      // Dòng tổng A./B./C. tô nền nhạt + in đậm giống cách hiển thị trên UI
      const isSummaryRow =
        !isHeaderRow &&
        !isSubCriteria(
          String(tableData[r - EXCEL_HEADER_ROWS]?.tieu_chi ?? ""),
        );

      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (!cell) continue;

        cell.s = {
          font: {
            name: "Calibri",
            sz: 11,
            bold: isHeaderRow || isSummaryRow,
            color: { rgb: isHeaderRow ? "FFFFFF" : "212529" },
          },
          fill: {
            patternType: "solid",
            fgColor: {
              rgb: isHeaderRow
                ? EXCEL_HEADER_FILLS[r]
                : isSummaryRow
                  ? EXCEL_SUMMARY_FILL
                  : "FFFFFF",
            },
          },
          border: EXCEL_BORDER,
          alignment: {
            // Cột Tiêu chí căn trái ở phần dữ liệu, còn lại căn giữa hết
            horizontal: c === 0 && !isHeaderRow ? "left" : "center",
            vertical: "center",
            wrapText: true,
          },
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Báo cáo bảo hành");

    const _name = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `bao_cao_tiep_nhan_xu_ly_bao_hanh_${_name}.xlsx`);
    NotificationExtension.Success("Export Excel thành công !");
  };

  useEffect(() => {
    fetchBranchNames();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (130 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data: tableData,
    enableColumnResizing: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableSorting: false,
    enableColumnFilters: false,
    enableFilters: false,
    enableColumnActions: false,
    enableRowNumbers: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    localization: _localization,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      density: "xs",
      columnPinning: { left: ["tieu_chi"] },
    },
    state: {
      columnOrder,
      isLoading,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantineTableContainerProps: {
      // height cố định (không chỉ min/max) để table height:100% resolve được -> hàng dàn đều full chiều cao
      style: { height: height, minHeight: height, maxHeight: height },
    },
    mantineTableProps: {
      withColumnBorders: true,
      withTableBorder: true,
      striped: true,
      highlightOnHover: true,
      // Cho bảng cao full container để các hàng tự dàn đều lấp hết chiều cao
      style: { height: "100%" },
    },
    mantineTableHeadCellProps: {
      // align="center" mới là thứ căn giữa nội dung header của MRT (nhóm cột đã tự căn giữa,
      // cột lá như "Tiêu chí" thì không) - textAlign đơn thuần không đủ
      align: "center",
      style: {
        fontSize: "13px",
        textAlign: "center",
        verticalAlign: "middle",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        fontSize: "13px",
        padding: "5px 12px",
        verticalAlign: "middle",
      },
    },
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileExport size={14} />}
              disabled={tableData.length === 0}
              onClick={() => handleExportExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Tháng"
              leftSection={<IconCalendarMonth color="#15aabf" />}
              data={dataMonth}
              value={search.thang1 || ""}
              searchable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : "", "thang1", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Năm"
              leftSection={<IconCalendarTime color="#15aabf" />}
              defaultValue={nam1 || ""}
              onChange={(e) => setNam1(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Tháng"
              leftSection={<IconCalendarMonth color="#15aabf" />}
              data={dataMonth}
              value={search.thang2 || ""}
              searchable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              onChange={(e) => {
                changeSearchValue(e ? e : "", "thang2", setSearch);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Năm"
              leftSection={<IconCalendarTime color="#15aabf" />}
              defaultValue={nam2 || ""}
              onChange={(e) => setNam2(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10} className="warranty-report-table">
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default WarrantyReceptionProcessingReport;
